import type { GroupedOffer } from '@aviata/platform-client';

import { FeatureLabelKey } from '../config';
import type { OfferRankingEntry } from '../types';

const FEATURE_LABEL_KEYS: FeatureLabelKey[] = Object.values(FeatureLabelKey);
const FEATURE_WEIGHT: Record<FeatureLabelKey, number> = {
  recommended: 4,
  fastest: 1,
  cheapest: 2,
};

const has = (offer: GroupedOffer, feature: FeatureLabelKey) =>
  offer.features?.[feature]?.value || offer.mergedFeatures?.[feature]?.value;

const hasRecommended = (offer: GroupedOffer) =>
  has(offer, FeatureLabelKey.RECOMMENDED);

export type GetWeightOptions = {
  /**
   * When set, only `recommended` is taken from offer features; `fastest` / `cheapest`
   * weights use these ranking tags (variant B).
   */
  rankingTags?: string[];
};

export const getWeight = (offer: GroupedOffer) => {
  return FEATURE_LABEL_KEYS.filter(feature => has(offer, feature))
    .map(feature => FEATURE_WEIGHT[feature])
    .reduce((a, b) => a + b, 0);
};

export const sortOffers = (offers: GroupedOffer[]) => {
  const sorted = offers
    .map(offer => ({ offer, weight: getWeight(offer) }))
    .sort((v1, v2) => v2.weight - v1.weight)
    .map(({ offer }) => offer);

  const recommended = sorted.filter(hasRecommended);
  const others = sorted.filter(offer => !hasRecommended(offer));

  return [...recommended, ...others];
};

export const compareGroupedOffersByRanking = (
  a: GroupedOffer,
  b: GroupedOffer,
  ranking: OfferRankingEntry[],
): number => {
  const aRecommended = hasRecommended(a);
  const bRecommended = hasRecommended(b);
  if (aRecommended !== bRecommended) {
    return aRecommended ? -1 : 1;
  }

  const rankById = new Map(ranking.map(r => [r.offer_id, r.rank]));

  const rankA = rankById.get(a.id);
  const rankB = rankById.get(b.id);

  return (
    (rankA ?? Number.MAX_SAFE_INTEGER) - (rankB ?? Number.MAX_SAFE_INTEGER)
  );
};

export const sortOffersByRanking = (
  offers: GroupedOffer[],
  ranking: OfferRankingEntry[] | undefined,
): GroupedOffer[] => {
  if (!ranking?.length) {
    return sortOffers(offers);
  }
  return [...offers].sort((a, b) =>
    compareGroupedOffersByRanking(a, b, ranking),
  );
};
